import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  EventAvailable as EventIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchAnalytics, setPeriod, setDateRange, exportData } from '../../redux/analyticsSlice';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import ToastService from '../../core/notifications/ToastService';

const COLORS = ['#14B8A6', '#F59E0B', '#EF4444', '#8B5CF6', '#10B981', '#3B82F6'];

export const AnalyticsPage: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { data, isLoading, error, selectedPeriod } = useSelector((state: RootState) => state.analytics);
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const loadAnalytics = useCallback(() => {
    if (!gymId) return;

    if (selectedPeriod === 'custom' && customDateFrom && customDateTo) {
      dispatch(fetchAnalytics({ gymId, dateFrom: customDateFrom, dateTo: customDateTo }));
    } else {
      dispatch(fetchAnalytics({ gymId }));
    }
  }, [gymId, selectedPeriod, customDateFrom, customDateTo, dispatch]);

  useEffect(() => {
    if (gymId) {
      loadAnalytics();
    }
  }, [gymId, loadAnalytics]);

  const handlePeriodChange = (period: string) => {
    dispatch(setPeriod(period));
  };

  const handleCustomDateApply = () => {
    if (customDateFrom && customDateTo) {
      dispatch(setDateRange({ from: customDateFrom, to: customDateTo }));
      dispatch(fetchAnalytics({ gymId: gymId!, period: 'custom', dateFrom: customDateFrom, dateTo: customDateTo }));
    }
  };

  const handleExport = async (dataType: string) => {
    if (!gymId) return;
    try {
      await dispatch(exportData({ gymId, dateFrom: customDateFrom, dateTo: customDateTo, dataType })).unwrap();
      ToastService.success('Data exported successfully');
    } catch (err: any) {
      ToastService.error(err || 'Failed to export data');
    }
  };

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString()}`;
  };

  if (isLoading && !data) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Track performance, trends, and insights
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => handleExport('daily_trends')}
        >
          Export Data
        </Button>
      </Box>

      {/* Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  value={selectedPeriod}
                  label="Period"
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedPeriod === 'custom' && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCustomDateApply}
                    sx={{ backgroundColor: '#14B8A6', height: '56px' }}
                  >
                    Apply
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Total Bookings
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {data.summary.total_bookings}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    {data.summary.completed_bookings} completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MoneyIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Total Revenue
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {formatCurrency(data.summary.total_revenue)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    From all bookings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Occupancy Rate
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {data.summary.occupancy_rate}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    Capacity utilization
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Avg Rating
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {data.summary.avg_rating}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    Customer satisfaction
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            {/* Booking Trends */}
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Booking Trends
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.trends.daily_bookings}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="bookings" stroke="#14B8A6" strokeWidth={2} name="Bookings" />
                      <Line type="monotone" dataKey="occupancy" stroke="#F59E0B" strokeWidth={2} name="Occupancy %" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Breakdown */}
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Activity Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.activity_breakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.activity}: ${entry.percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="bookings"
                      >
                        {data.activity_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Revenue Trends */}
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Revenue Trends
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.trends.daily_bookings}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="revenue" fill="#F59E0B" name="Revenue (₹)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Customer Metrics */}
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Customer Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>Total Customers</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                        {data.customer_metrics.total_customers}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>Repeat Rate</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#F59E0B' }}>
                        {data.customer_metrics.repeat_rate}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>New Customers</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                        {data.customer_metrics.new_customers}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>Avg Bookings/Customer</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#8B5CF6' }}>
                        {data.customer_metrics.avg_bookings_per_customer}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Peak Hours */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Peak Hours Analysis
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.peak_hours}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="bookings" fill="#14B8A6" name="Bookings" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default AnalyticsPage;
